/**
 * Navbar component - Global navigation header for employee pages
 *
 * Provides consistent navigation across authenticated employee pages.
 */

import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../../hooks/useAuth'
import { useNotifications } from '../../hooks/useNotifications'
import { BrandMark } from '@/common/components/brand-mark'
import * as styles from './Navbar.css'

const employeeNavItems = [
  { label: 'Dashboard', to: '/' as const },
  { label: 'Timesheet', to: '/timesheets' as const },
  { label: 'History', to: '/history' as const },
  { label: 'Calendar', to: '/calendar' as const },
]

export function Navbar() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { unreadCount } = useNotifications()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : 'User'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    setIsDropdownOpen(false)
    await logout()
    window.location.href = '/'
  }

  const handleBellClick = () => {
    if (location.pathname === '/') {
      document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate({ to: '/' }).then(() => {
        setTimeout(() => {
          document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth' })
        }, 300)
      })
    }
  }

  return (
    <nav className={styles.navbar}>
      <div className={styles.content}>
        <div className={styles.left}>
          <Link to="/" className={styles.brand}>
            <BrandMark />
          </Link>

          <div className={styles.links}>
            {employeeNavItems.map((item) => {
              const isActive =
                item.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.to)

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`${styles.link} ${isActive ? styles.linkActive : ''}`}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>
        </div>

        <div className={styles.right}>
          <button
            className={styles.bellBtn}
            onClick={handleBellClick}
            aria-label="Notifications"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unreadCount > 0 && (
              <span className={styles.bellBadge}>{unreadCount}</span>
            )}
          </button>

          <div className={styles.dropdownContainer} ref={dropdownRef}>
            <button
              className={styles.userBtn}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              <span className={styles.userName}>{displayName}</span>
            </button>

            {isDropdownOpen && (
              <div className={styles.dropdownMenu}>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
